//REACT
import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

// YARN
import { toast } from 'react-toastify';
import {
  Button,
  Checkbox,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  ThemeProvider,
} from '@mui/material';
import Settings from '@mui/icons-material/Tune';

// SERVICES
import { sendEvent } from 'utils/Event';
import CustomDataGridResult from 'utils/Datagrid/CustomForecastDataGridResult';
import {
  CustomCropToPlantHeader,
  CustomEditBrushAutomaticEffectCell,
  CustomEffectEditCell,
  brushValueList,
  customCropToPlantCell,
  handleRowUpdateValidation,
  themeRotateEffectsFilter,
} from 'utils/Datagrid/CustomRotateMatricesDataGridUtils';
import { inputFloatValidatorRotateEffect } from 'components/generics/Validators/validator';
import {
  checkThreeValueAndDecimalRegexTwoDigitsDatagrid,
  displayErrorMessage,
  getNewKeyBetweenObjects,
} from 'utils/tools_functions';
import { updateGroundTypeEffect } from 'services/API/Cooperative';
import { StyledBadge } from 'utils/Datagrid/CustomCultureDataGridUtils';
import { validateDatagridMatrix } from 'components/generics/Validators/datagrid.validate';
import useFilteredRotationsLocalStorage from 'utils/Datagrid/useFilteredRotationsLocalStorage';
import useDatagridMatricesFilter from '../../../utils/Datagrid/useDatagridMatricesFilter';
import useDataGridMatricesCellBehaviour from 'utils/Datagrid/useDataGridMatricesCellBehaviour';
import useDataGridMatricesBrushMode from 'utils/Datagrid/useDataGridMatricesBrushMode';
import { CustomTooltipEffects } from 'utils/Datagrid/Effects/CustomEffectsDataGridUtils';
import brushLogo from 'assets/images/brush.svg';
import ZoomInMapIcon from '@mui/icons-material/ZoomInMap';
import { CustomToolbarEffects } from '../../../utils/Datagrid/Effects/CustomEffectsDataGridUtils';
import { customButtonTheme } from 'assets/styles/themes/generic_button_mui';

// THEMES
import './dataGridRotateEffects.style.scss';
import useToggleSwitch from 'utils/hooks/useToggleSwitch';
import { rotateEffectTheme } from 'utils/Datagrid/CustomForecastDataGridUtils';

const DataGridGroundTypeEffects = ({
  cultures,
  refreshData,
  ownerId,
  from,
  groundTypes,
  handleResetGroundTypeEffects,
}) => {
  const [rows, setRows] = useState([]);
  const [columns, setColumns] = useState([]);
  const [allowSubmitEffect, setAllowSubmitEffect] = useState(false);
  const stackedRequestsRef = useRef([]);
  const [isLoading, setIsLoading] = useState(false);
  const [fullScreen, setFullScreen] = useState(false);
  const [errorsEffectFormDefault, setErrorsEffectFormDefault] = useState({
    formError: false,
    effect: {
      message: '',
      validator: [inputFloatValidatorRotateEffect],
    },
    cellErrors: {},
  });

  const [editedRows, setEditedRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [hasClickAway, setHasClickAway] = useState(true);

  const handleFullScreen = () => {
    setFullScreen(!fullScreen);
  };

  const { renderToggleSwitch, isOrganic } = useToggleSwitch(
    from,
    'groundTypesEffects',
    refreshData,
    ownerId,
    stackedRequestsRef,
    errorsEffectFormDefault?.formError,
  );

  const stateIsOrganic = isOrganic ? 'organic' : 'conventional';
  const { t } = useTranslation();

  const {
    toggleFilter,
    setToggleFilter,
    selectedFilters,
    setSelectedFilters,
    selectAllFilters,
    setSelectAllFilters,
  } = useFilteredRotationsLocalStorage(
    isOrganic,
    stateIsOrganic,
    Number(ownerId),
    from,
    cultures,
    'groundTypeEffect',
    groundTypes,
  );

  const { renderCell, onCellEditStop, rowModesModel, setRowModesModel, handleRowModesModelChange } =
    useDataGridMatricesCellBehaviour({
      stackedRequestsRef,
      errorsEffectFormDefault,
      setErrorsEffectFormDefault,
      validateDatagridMatrix,
      hasClickAway,
      setHasClickAway,
    });

  const {
    modeBrush,
    setModeBrush,
    allowSubmitEffectBrush,
    setAllowSubmitEffectBrush,
    selectedBrushValue,
    setSelectedBrushValue,
    handleBrushEffect,
    handleClickValue,
  } = useDataGridMatricesBrushMode();

  useEffect(() => {
    // Dismount
    return () => {
      sendEvent('cancelHttpRequest');
    };
  }, []);

  const processedRows = useMemo(() => {
    return cultures?.map((cultureInRow) => {
      let processedRow = {
        id: cultureInRow.id,
        cropToPlant: cultureInRow.name,
        groundTypeEffects: cultureInRow.groundTypeEffects,
      };

      return processedRow;
    });
  }, [cultures]);

  useEffect(() => {
    setIsLoading(true);
    const filteredRows = processedRows?.filter((culture) => {
      return selectedFilters?.[stateIsOrganic]?.rows?.[culture.id] !== false;
    });
    const filteredColumns = groundTypes?.filter((groundType) => {
      return selectedFilters?.[stateIsOrganic]?.columns?.[groundType.id] !== false;
    });
    if (
      filteredRows?.length !== processedRows?.length ||
      filteredColumns?.length !== groundTypes?.length
    ) {
      processedRows && setRows(filteredRows);
      processedRows && setColumns(filteredColumns);
    } else {
      processedRows && setRows(processedRows);
      processedRows && setColumns(groundTypes);
    }
    setIsLoading(false);
  }, [cultures, groundTypes, stateIsOrganic, selectedFilters]);

  useEffect(() => {
    if (processedRows && processedColumns && !isLoading) {
      const filteredRows = processedRows?.filter(
        (row) => selectedFilters?.[stateIsOrganic]?.rows?.[row.id],
      );
      const columnsToFilter = groundTypes?.filter(
        (groundType) => selectedFilters?.[stateIsOrganic]?.columns?.[groundType.id],
      );
      const updatedFilteredRows = filteredRows.map((row) => {
        if (editedRows[row.id]) {
          return { ...row, ...editedRows[row.id] };
        }
        return row;
      });
      setRows(updatedFilteredRows || []);
      setColumns(columnsToFilter || []);
    }
  }, [selectedFilters, editedRows, stateIsOrganic]);

  const { handleOnChangeFilter, renderFilterMenuItems, handleToggleFilter, isIndeterminate } =
    useDatagridMatricesFilter({
      stateIsOrganic,
      selectedFilters,
      setSelectedFilters,
      selectAllFilters,
      setSelectAllFilters,
      setToggleFilter,
    });

  const renderEditBrushAutomaticEffectCell = (params) => {
    return (
      <CustomEditBrushAutomaticEffectCell selectedBrushValue={selectedBrushValue} params={params} />
    );
  };

  const renderEditEffectCell = (params) => {
    return (
      <CustomEffectEditCell
        params={params}
        errorsEffectFormDefault={errorsEffectFormDefault}
        setRowModesModel={setRowModesModel}
        setHasClickAway={setHasClickAway}
      />
    );
  };

  const processColumns =
    columns?.map((groundType) => {
      return {
        field: groundType.id.toString(),
        id: groundType.id,
        headerName: groundType.name,
        headerClassName: 'cropEffect-header',
        editable: true,
        sortable: false,
        headerAlign: 'center',
        align: 'center',
        flex: 1,
        type: 'number',
        minWidth: 80,
        cellClassName: 'viewModeCell',
        renderCell: renderCell,
        renderEditCell:
          modeBrush && selectedBrushValue !== null
            ? renderEditBrushAutomaticEffectCell
            : renderEditEffectCell,
        valueGetter: (value, row) => {
          return row?.groundTypeEffects?.find(
            (groundTypeEffect) => groundTypeEffect?.groundType?.id === groundType?.id,
          )?.effect;
        },
        valueParser: (value) => {
          return checkThreeValueAndDecimalRegexTwoDigitsDatagrid(value);
        },
      };
    }) ?? [];

  const cropToPlantColumn = {
    field: 'cropToPlant',
    headerName: t('column.cropToPlant'),
    headerClassName: 'cropToPlant-header',
    renderHeader: CustomCropToPlantHeader,
    renderCell: customCropToPlantCell,
    cellClassName: 'cropToPlant-cell',
    sortable: false,
    headerAlign: 'center',
    align: 'center',
    flex: 1,
    minWidth: 140,
  };

  const noColumns = {
    field: 'noColumns',
    headerName: '',
    headerClassName: 'cropEffect-header',
    renderCell: (params) => {
      if (params.row.id === rows[0]?.id) {
        return <div className="noColumns">{t('label.noColumnsToDisplay')}</div>;
      }
      return null;
    },
    cellClassName: 'viewModeCell-noColumns',
    sortable: false,
    headerAlign: 'center',
    align: 'center',
    flex: 1,
    minWidth: 140,
  };

  const processedColumns = [
    cropToPlantColumn,
    ...(processColumns && processColumns?.length > 0 ? processColumns : [noColumns]),
  ];

  const originalRowsCount = processedRows?.length;
  const countFilteredRows = originalRowsCount - rows.length;

  const countFilteredColumns = groundTypes?.length - columns?.length;

  const processRowUpdate = useCallback(
    async (updatedRow, previousRow) => {
      errorsEffectFormDefault.formError = false;
      const toUpdate = getNewKeyBetweenObjects(previousRow, updatedRow);

      if (!toUpdate) {
        return previousRow;
      }

      const previousCrop = Number(Object.keys(toUpdate)[0]);
      const isEffectUpdated =
        previousRow.groundTypeEffects.find(
          (groundTypeEffect) => groundTypeEffect?.groundType?.id === previousCrop,
        ).effect !== toUpdate[previousCrop];

      const isEffectSameAsOriginalEffect =
        cultures
          .find((culture) => culture?.id === updatedRow?.id)
          ?.groundTypeEffects?.find(
            (groundTypeEffect) => groundTypeEffect?.groundType?.id === previousCrop,
          )?.effect === toUpdate[previousCrop];

      if (isEffectUpdated) {
        const formatedToUpdateObject = { ...toUpdate };
        const formatedToUpdateObjectDescriptor = Object.getOwnPropertyDescriptor(
          formatedToUpdateObject,
          previousCrop,
        );

        if (formatedToUpdateObjectDescriptor === undefined) {
          return previousRow;
        }

        Object.defineProperty(formatedToUpdateObject, 'effect', formatedToUpdateObjectDescriptor);
        delete formatedToUpdateObject[previousCrop];

        setErrorsEffectFormDefault({
          ...validateDatagridMatrix(
            formatedToUpdateObject,
            errorsEffectFormDefault,
            updatedRow.id,
            previousCrop,
          ),
        });

        updatedRow = {
          ...updatedRow,
          groundTypeEffects: updatedRow.groundTypeEffects.map((groundTypeEffect) => {
            return groundTypeEffect?.groundType?.id === previousCrop
              ? {
                  ...groundTypeEffect,
                  effect: toUpdate[previousCrop],
                }
              : groundTypeEffect;
          }),
        };
        delete updatedRow[previousCrop];

        const updatedEffect = updatedRow.groundTypeEffects.find(
          (groundTypeEffect) => groundTypeEffect.groundType.id === previousCrop,
        );

        let existInStackedRequest = false;
        stackedRequestsRef.current.forEach((request) => {
          if (request?.uriParams?.rotateEffectId === updatedEffect?.id) {
            existInStackedRequest = true;
          }
        });

        await handleRowUpdateValidation(updatedRow, errorsEffectFormDefault);

        if (!existInStackedRequest) {
          stackedRequestsRef.current.push({
            uriParams: {
              ownerId: ownerId,
              rotateEffectId: updatedEffect?.id,
            },
            body: {
              id: updatedEffect?.id,
              from: updatedEffect?.from,
              effect: updatedEffect?.effect,
              isOrganic: isOrganic,
            },
            cellInformations: {
              rowId: updatedRow.id,
              columnId: previousCrop,
              rotateEffectId: updatedEffect?.id,
              effect: updatedEffect?.effect,
            },
          });
        } else if (!isEffectSameAsOriginalEffect) {
          stackedRequestsRef.current = stackedRequestsRef.current.map((request) => {
            if (request.uriParams.rotateEffectId === updatedEffect?.id) {
              return {
                ...request,
                body: {
                  ...request.body,
                  effect: updatedEffect?.effect,
                },
                cellInformations: {
                  ...request.cellInformations,
                  effect: updatedEffect?.effect,
                },
              };
            } else {
              return request;
            }
          });
        } else {
          let tempStackedRequests = [];
          stackedRequestsRef.current.forEach((request) => {
            if (request.cellInformations.rotateEffectId !== updatedEffect?.id) {
              tempStackedRequests.push(request);
            }
          });
          stackedRequestsRef.current = tempStackedRequests;
        }

        setAllowSubmitEffect(stackedRequestsRef?.current?.length > 0);
        if (modeBrush) {
          setAllowSubmitEffectBrush(true);
        }

        setEditedRows((prev) => ({
          ...prev,
          [updatedRow.id]: updatedRow,
        }));

        return updatedRow;
      }

      return previousRow;
    },
    [cultures, stackedRequestsRef, rows, editedRows],
  );

  const callAPI = async () => {
    setIsLoading(true);
    if (Object.keys(errorsEffectFormDefault?.cellErrors)?.length !== 0) {
      toast.warning(t('errors.groundTypeEffectsNotSaved'));
    }

    const requestBody = stackedRequestsRef.current
      .map((request) => request.body)
      .filter((request) => request);

    try {
      await updateGroundTypeEffect(from, ownerId, requestBody);
      setAllowSubmitEffect(false);
      setAllowSubmitEffectBrush(false);
      setModeBrush(false);
      setSelectedBrushValue(null);
      setEditedRows([]);
    } catch (error) {
      if (error) {
        toast.error(
          displayErrorMessage(error?.response?.data?.message ?? 'ERR_UPDATE_GROUND_TYPE_EFFECT'),
        );
      }
    }
    stackedRequestsRef.current = [];
    refreshData(isOrganic, ownerId, false, 'rotation');

    toast.success(t('success.groundTypeEffectsUpdated'));
  };

  const filterMenuItems = cultures?.map((culture) =>
    renderFilterMenuItems(culture, selectedFilters?.[stateIsOrganic]?.rows),
  );
  const filterMenuColumnsItems = groundTypes?.map((groundType) =>
    renderFilterMenuItems(groundType, selectedFilters?.[stateIsOrganic]?.columns),
  );

  const handleTooltipToggle = () => {
    setOpen((open) => !open);
  };

  return (
    cultures && (
      <div className="section">
        <div className="main_container">
          <div className="override">
            {from !== 'exploitations' && (
              <h1 className="title_section title_section_culture title_rotate_effects">
                {t('title.groundTypeMatrix')}
              </h1>
            )}
          </div>
          <div
            className={`datagrid_container rotate_effects_container ${
              modeBrush ? 'modeBrush' : ''
            }`}
          >
            <div className={`${fullScreen ? 'fullscreen' : ''}`}>
              <div
                className={`${
                  fullScreen && toggleFilter
                    ? 'contain_brush_effect_fullscreen contain_brush_effect'
                    : 'contain_brush_effect'
                }`}
              >
                {fullScreen && (
                  <div>
                    <ThemeProvider theme={customButtonTheme}>
                      <Button
                        disabled={!allowSubmitEffectBrush}
                        onClick={handleBrushEffect}
                        sx={{ ml: 2 }}
                      >
                        {t('button.finish')}
                        <span>
                          <img src={brushLogo} alt="Logo pinceau" className="brush_logo" />
                        </span>
                      </Button>
                    </ThemeProvider>
                  </div>
                )}
                {!fullScreen && <div>{renderToggleSwitch()}</div>}
                <ThemeProvider theme={themeRotateEffectsFilter}>
                  <div className="filter_container_rotate_effects">
                    <div className="button_filters_effects" onClick={handleToggleFilter}>
                      <span>{toggleFilter ? '' : t('common.showFilters')}</span>
                      <Settings className="settings-icon" />
                    </div>
                    {toggleFilter && (
                      <div className="d-flex">
                        <div>
                          <FormControl className="formControl">
                            <InputLabel shrink={false} id="filterLabel" className="input-label">
                              {t('label.filterRows')}
                            </InputLabel>
                            <Select
                              labelId="filterLabel"
                              id="filter"
                              value={Object.keys(
                                selectedFilters?.[stateIsOrganic]?.rows || {},
                              ).filter(
                                (id) => selectedFilters?.[stateIsOrganic]?.rows?.[Number(id)],
                              )}
                              onChange={(event) =>
                                handleOnChangeFilter(
                                  typeof event.target.value === 'string'
                                    ? [event.target.value]
                                    : event.target.value,
                                  'rows',
                                  cultures,
                                )
                              }
                              multiple
                              renderValue={() => ''}
                              MenuProps={{ autoFocus: false }}
                            >
                              <MenuItem value="all">
                                <Checkbox
                                  indeterminate={isIndeterminate('rows')}
                                  checked={selectAllFilters?.[stateIsOrganic]?.rows}
                                />
                                <ListItemText primary={t('common.selectAll')} />
                              </MenuItem>
                              {filterMenuItems}
                            </Select>
                            <StyledBadge
                              badgeContent={countFilteredRows}
                              color="primary"
                            ></StyledBadge>
                          </FormControl>
                        </div>
                        <div>
                          <FormControl className="formControl">
                            <InputLabel shrink={false} id="filterLabel" className="input-label">
                              {t('label.filterColumns')}
                            </InputLabel>
                            <Select
                              labelId="filterLabel"
                              id="filter"
                              value={Object.keys(
                                selectedFilters?.[stateIsOrganic]?.columns || {},
                              )?.filter(
                                (id) => selectedFilters?.[stateIsOrganic].columns?.[Number(id)],
                              )}
                              onChange={(event) =>
                                handleOnChangeFilter(
                                  typeof event.target.value === 'string'
                                    ? [event.target.value]
                                    : event.target.value,
                                  'columns',
                                  groundTypes,
                                )
                              }
                              multiple
                              renderValue={() => ''}
                              MenuProps={{ autoFocus: false }}
                            >
                              <MenuItem value="all">
                                <Checkbox
                                  indeterminate={isIndeterminate('columns')}
                                  checked={selectAllFilters?.[stateIsOrganic]?.columns}
                                />
                                <ListItemText primary={t('common.selectAll')} />
                              </MenuItem>
                              {filterMenuColumnsItems}
                            </Select>
                            <StyledBadge
                              badgeContent={countFilteredColumns}
                              color="primary"
                            ></StyledBadge>
                          </FormControl>
                        </div>
                      </div>
                    )}
                  </div>
                </ThemeProvider>
                <CustomTooltipEffects
                  open={open}
                  setOpen={setOpen}
                  handleTooltipToggle={handleTooltipToggle}
                  brushValueList={brushValueList}
                  selectedBrushValue={selectedBrushValue}
                  handleClickValue={handleClickValue}
                  modeBrush={modeBrush}
                  setSelectedBrushValue={setSelectedBrushValue}
                />
                {fullScreen && (
                  <div className="contain_switch_effect handle-full-screen">
                    <ZoomInMapIcon onClick={handleFullScreen} />
                  </div>
                )}
              </div>
              <CustomDataGridResult
                theme={rotateEffectTheme}
                rows={rows}
                fromRotateEffects
                columns={processedColumns}
                processRowUpdate={processRowUpdate}
                onCellEditStop={onCellEditStop}
                handleRowModesModelChange={handleRowModesModelChange}
                rowModesModel={rowModesModel}
                setRowModesModel={setRowModesModel}
                fullSize={true}
                from={from}
                stackedRequestsRef={stackedRequestsRef}
                getRowClassName={(params) => {
                  const culture = cultures?.find((culture) => culture.id === params.row.id);
                  return culture?.cultureType === 'intermediate' ? 'intermediate' : '';
                }}
                customToolbar={
                  <CustomToolbarEffects
                    handleFullScreen={handleFullScreen}
                    callAPI={callAPI}
                    allowSubmitEffect={allowSubmitEffect}
                    allowSubmitEffectBrush={allowSubmitEffectBrush}
                    handleBrushEffect={handleBrushEffect}
                    fullScreen={fullScreen}
                    handleResetGroundTypeEffects={handleResetGroundTypeEffects}
                    fromGroundTypeEffects={true}
                    from={from}
                  />
                }
              />
            </div>
          </div>
        </div>
      </div>
    )
  );
};

export default DataGridGroundTypeEffects;
